/**
 * waveformSvg — a pure, zero-dependency, SSR-safe builder for a SoundCloud-style
 * bar waveform as an INLINE `<svg>` string. Used identically by the editor audio
 * player and the published-site renderer (via the `@website-builder/editor/
 * waveformSvg` subpath export), so both draw the exact same picture.
 *
 * Inline (not a `data:` URI like svgPreview.ts) on purpose: the progress fill is
 * done by stacking this SVG twice — a gray BASE and an accent PLAYED copy — both
 * with `fill="currentColor"` so a wrapping element's `color` picks the shade, and
 * the played copy revealed left→right by a single CSS var (`--wb-pct`) via
 * `clip-path`. `currentColor` + CSS clipping only work on inline SVG in the DOM.
 *
 * All bars are ONE `<path>` (not N rects) to bound DOM nodes + bytes: ~400 bars
 * ≈ 9 KB. Peaks are ints 0..100 (normalized amplitude); coords rounded to 2dp.
 */

export interface WaveformOpts {
  /** viewBox width (unitless; the svg is width:100% and scales). */
  readonly width?: number;
  /** viewBox height. */
  readonly height?: number;
  /** Fraction of each bar's slot that is the gap between bars (0..1). */
  readonly gap?: number;
  /** Minimum bar height as a fraction of height, so silence still shows. */
  readonly minH?: number;
}

/** Round to 2dp — keeps the path string compact. */
function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function waveformSvg(
  peaks: readonly number[],
  opts: WaveformOpts = {},
): string {
  const W = opts.width ?? 1000;
  const H = opts.height ?? 80;
  const gap = opts.gap ?? 0.3;
  const minH = opts.minH ?? 0.06;
  const n = peaks.length || 1;
  const slot = W / n;
  const bw = slot * (1 - gap);
  const cy = H / 2;

  let d = "";
  for (let i = 0; i < peaks.length; i++) {
    const p = Math.max(0, Math.min(100, peaks[i] ?? 0));
    const barH = minH * H + (p / 100) * (1 - minH) * H;
    const x = r2(i * slot + (slot - bw) / 2);
    const y = r2(cy - barH / 2);
    const h = r2(barH);
    // A sharp rectangle as a closed subpath.
    d += `M${x} ${y}h${r2(bw)}v${h}h-${r2(bw)}z`;
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" ` +
    `preserveAspectRatio="none" width="100%" height="100%" aria-hidden="true">` +
    `<path d="${d}" fill="currentColor"/></svg>`
  );
}

/**
 * A gentle low-amplitude pattern shown when real peaks aren't available yet
 * (decode failed / unsupported codec, or an asset picked from the library whose
 * peaks haven't been computed). Reads as "waveform, no data" rather than blank.
 */
export const FLAT_PLACEHOLDER: readonly number[] = Array.from(
  { length: 200 },
  (_, i) => 8 + Math.round(6 * Math.abs(Math.sin(i / 7))),
);
